import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, ClipboardList, CheckCircle, XCircle, Clock, AlertTriangle, DollarSign, Edit, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import ProcurementService from '@/api/procurement.service';
import { canCreate, canEdit, canView } from '@/lib/permissions';

interface PurchaseRequisition {
  id: number;
  publicId: string;
  businessId: number;
  requisitionNumber: string;
  requisitionType: 'inventory' | 'services' | 'equipment';
  requestedBy: string;
  requestedByName?: string;
  departmentId: number;
  departmentName?: string;
  justification: string;
  urgencyLevel: 'urgent' | 'normal' | 'low';
  status: 'pendingDepartment' | 'pendingSenior' | 'budgetVerification' | 'approved' | 'rejected' | 'budgetInsufficient';
  totalAmount: number;
  currency: string;
  departmentApproved: boolean;
  departmentApprovedBy?: string;
  departmentApprovedDate?: string;
  seniorApproved: boolean;
  seniorApprovedBy?: string;
  seniorApprovedDate?: string;
  budgetVerified: boolean;
  budgetReservationId?: string;
  rejectionReason?: string;
  dateCreated: string;
  dateUpdated: string;
  items: RequisitionItem[];
}

interface RequisitionItem {
  id: number;
  itemDescription: string;
  quantity: number;
  unit: string;
  estimatedCost: number;
  specifications?: string;
  preferredSupplierId?: number;
  preferredSupplierName?: string;
}

const PurchaseRequisitions: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requisitions, setRequisitions] = useState<PurchaseRequisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedUrgency, setSelectedUrgency] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchRequisitions();
  }, [businessId, currentPage, selectedStatus]);

  const fetchRequisitions = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await ProcurementService.getAllPurchaseRequisitions(
        businessId, 
        selectedStatus || undefined, 
        skip, 
        itemsPerPage
      );
      
      if (response.data.result === 'success') {
        setRequisitions(response.data?.requisitions || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch purchase requisitions",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching purchase requisitions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch purchase requisitions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requisition: PurchaseRequisition) => {
    try {
      await ProcurementService.approvePurchaseRequisition(requisition.publicId, {
        approvalLevel: requisition.status === 'pendingDepartment' ? 'department' : 'senior',
        comments: 'Approved via web interface'
      });
      toast({
        title: "Success",
        description: "Requisition approved successfully",
      });
      fetchRequisitions();
    } catch (error) {
      console.error('Error approving requisition:', error);
      toast({
        title: "Error",
        description: "Failed to approve requisition",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (requisition: PurchaseRequisition) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;

    try {
      await ProcurementService.rejectPurchaseRequisition(requisition.publicId, {
        rejectionReason: reason
      });
      toast({
        title: "Success",
        description: "Requisition rejected successfully",
      });
      fetchRequisitions();
    } catch (error) {
      console.error('Error rejecting requisition:', error);
      toast({
        title: "Error",
        description: "Failed to reject requisition",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pendingDepartment':
        return <Badge variant="secondary">Pending Department</Badge>;
      case 'pendingSenior':
        return <Badge variant="secondary">Pending Senior</Badge>;
      case 'budgetVerification':
        return <Badge variant="outline">Budget Check</Badge>;
      case 'approved':
        return <Badge variant="default">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      case 'budgetInsufficient':
        return <Badge variant="destructive">Budget Insufficient</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case 'urgent':
        return <Badge variant="destructive">Urgent</Badge>;
      case 'normal':
        return <Badge variant="outline">Normal</Badge>;
      case 'low':
        return <Badge variant="secondary">Low</Badge>;
      default:
        return <Badge variant="outline">{urgency}</Badge>;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const filteredRequisitions = requisitions.filter(req => {
    const matchesSearch = !searchTerm || 
      req.requisitionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.justification.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesUrgency = !selectedUrgency || req.urgencyLevel === selectedUrgency;
    
    return matchesSearch && matchesUrgency;
  });

  const getTotalsByStatus = () => {
    return {
      total: requisitions.length,
      pending: requisitions.filter(r => r.status.includes('pending')).length,
      approved: requisitions.filter(r => r.status === 'approved').length,
      rejected: requisitions.filter(r => r.status === 'rejected').length,
      totalValue: requisitions.reduce((sum, r) => sum + r.totalAmount, 0)
    };
  };

  const stats = getTotalsByStatus();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Requisitions</h1>
          <p className="text-muted-foreground">
            Manage purchase requests and approval workflows
          </p>
        </div>
        {canCreate('Procurement') && (
          <Button onClick={() => navigate('/procurement/requisitions/create')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Requisition
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requisitions</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approved}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.rejected}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.totalValue, requisitions[0]?.currency || 'USD')}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by requisition number, requester, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="pendingDepartment">Pending Department</option>
                <option value="pendingSenior">Pending Senior</option>
                <option value="budgetVerification">Budget Verification</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="budgetInsufficient">Budget Insufficient</option>
              </select>
            </div>
            <div className="sm:w-32">
              <select
                value={selectedUrgency}
                onChange={(e) => setSelectedUrgency(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All Urgency</option>
                <option value="urgent">Urgent</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requisitions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Requisitions</CardTitle>
          <CardDescription>
            A list of all purchase requisitions and their approval status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requisition #</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequisitions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No purchase requisitions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequisitions.map((requisition) => (
                      <TableRow key={requisition.id}>
                        <TableCell className="font-mono text-sm">{requisition.requisitionNumber}</TableCell>
                        <TableCell>{requisition.requestedByName || requisition.requestedBy}</TableCell>
                        <TableCell>{requisition.departmentName || `Dept ${requisition.departmentId}`}</TableCell>
                        <TableCell>{getUrgencyBadge(requisition.urgencyLevel)}</TableCell>
                        <TableCell>{formatCurrency(requisition.totalAmount, requisition.currency)}</TableCell>
                        <TableCell>{getStatusBadge(requisition.status)}</TableCell>
                        <TableCell>{new Date(requisition.dateCreated).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {canView('Procurement') && (
                                <DropdownMenuItem onClick={() => navigate(`/procurement/requisitions/${requisition.publicId}`)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              {canEdit('Procurement') && requisition.status.includes('pending') && (
                                <>
                                  <DropdownMenuItem onClick={() => navigate(`/procurement/requisitions/edit/${requisition.publicId}`)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleApprove(requisition)}>
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleReject(requisition)}
                                    className="text-destructive"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseRequisitions; 